import React, { useState, useEffect, useRef, useCallback } from 'react';
import { documentsAPI } from '../../services/api';
import { deleteDocumentRequest } from '../../components/documents/DocumentFormModal';
import DocumentsTable from '../../components/documents/DocumentsTable';
import DocumentFormModal from '../../components/documents/DocumentFormModal';
import DocumentStatusModal from '../../components/documents/DocumentStatusModal';
import { changeDocumentStatus } from '../../services/api';
import ToastContainer, { showToast } from '../../components/common/Toast';
import ConfirmModal from '../../components/common/ConfirmModal';

import {
  PlusIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';


import DocumentStatisticsCards from '../../components/documents/cards/DocumentStatisticsCards';
import DocumentDemographicsDashboard from '../../components/documents/cards/DocumentDemographicsDashboard';
import CertificateTemplatesPage from '../../components/documents/CertificateTemplatesPage';
import CertificatePrintPreviewModal from '../../components/documents/CertificatePrintPreviewModal';

// 🧠 Documents Management Page
const DocumentsPage = () => {
  // State
  // Demographics dashboard toggle
  const [showDemographics, setShowDemographics] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({ document_type: '', status: '', date_from: '', date_to: '' });
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Certificate Template Modal
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // Certificate Preview Modal (for viewing/printing a document)
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  // Status modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  // Change status handler
  const handleChangeStatus = (doc) => {
    setStatusTarget(doc);
    setShowStatusModal(true);
  };

  const handleStatusSubmit = async (newStatus) => {
    if (!statusTarget) return;
    setStatusLoading(true);
    try {
      await changeDocumentStatus(statusTarget.id, newStatus, statusTarget);
      showToast('Status updated successfully.', 'success');
      setShowStatusModal(false);
      setStatusTarget(null);
      // Refetch documents and statistics after status update
      fetchDocuments();
      fetchStats();
    } catch (err) {
      showToast('Failed to update status.', 'error');
    } finally {
      setStatusLoading(false);
    }
  };


  // Fetch documents (refetchable)
  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: debouncedSearch || undefined,
        ...(filters.status && { status: filters.status }),
        ...(filters.document_type && { document_type: filters.document_type }),
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to }),
      };
      const { data } = await documentsAPI.getAll(params);
      setDocuments(data.data || []);
      // Pagination data is nested under data.pagination
      if (data.pagination) {
        setTotal(data.pagination.total || 0);
        setLastPage(data.pagination.last_page || 1);
      } else {
        // Fallback for backward compatibility
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      }
    } catch (err) {
      setDocuments([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1); // Reset to first page when searching
    }, 400); // 400ms delay

    return () => clearTimeout(timer);
  }, [search]);

  // Fetch documents when page, perPage, debouncedSearch, or filters change
  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, filters]);

  // Pagination handler
  const handlePageChange = (newPage, newPerPage = perPage) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const handleFilterChange = useCallback((newFilters) => {
    setFilters({
      document_type: newFilters.document_type || '',
      status: newFilters.status || '',
      date_from: newFilters.date_from || '',
      date_to: newFilters.date_to || '',
    });
    setPage(1);
  }, []);

  // Search handler (now just for UI)
  const handleSearch = () => {
    // Search is now handled by debounced effect
  };

  // Table action handlers
  const handleView = (doc) => {
    setPreviewDoc(doc);
    setShowPreviewModal(true);
  };

  const handleEdit = (doc) => {
    setEditData(doc);
    setShowForm(true);
  };

  const handleDelete = (doc) => {
    setConfirmTarget(doc);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    setConfirmLoading(true);
    await deleteDocumentRequest(
      confirmTarget.id,
      () => {
        setPage(1);
        fetchDocuments();
        fetchStats();
        setConfirmLoading(false);
        setConfirmOpen(false);
        setConfirmTarget(null);
      },
      () => {
        setConfirmLoading(false);
        setConfirmOpen(false);
        setConfirmTarget(null);
      }
    );
  };

  const handleTrack = (doc) => {
    // TODO: Implement tracking modal if needed
    alert(`Track document #${doc.tracking_number}`);
  };

  // Add new document
  const handleAdd = () => {
    setEditData(null);
    setShowForm(true);
  };

  // Form submit handler
  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      // Always send document_type as the backend expects
      const payload = {
        ...formData,
        document_type: formData.type || formData.document_type,
      };
      delete payload.type;
      if (editData) {
        await documentsAPI.update(editData.id, payload);
        showToast('Document updated successfully.', 'success');
      } else {
        await documentsAPI.create(payload);
        showToast('Document created successfully.', 'success');
      }
      setShowForm(false);
      // Refetch documents and statistics after add/update
      fetchDocuments();
      fetchStats();
    } catch (err) {
      showToast('Failed to save document.', 'error');
    } finally {
      setFormLoading(false);
    }
  };


  // --- Document statistics from API ---
  const [stats, setStats] = React.useState({});
  const fetchStats = React.useCallback(() => {
    let mounted = true;
    documentsAPI.getStatistics()
      .then(res => {
        const data = res.data && res.data.data ? res.data.data : res.data;
        if (mounted) setStats(data || {});
      })
      .catch(() => { if (mounted) setStats({}); });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const cleanup = fetchStats();
    return cleanup;
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12  mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-in fade-in duration-1000">
      <div className="max-w-9xl mx-auto p-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 animate-in slide-in-from-top duration-700">
          <div className="flex items-center space-x-4">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg animate-pulse">
              <DocumentTextIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Documents Management</h1>
              <p className="text-gray-600">Document Issuance & Tracking System</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowTemplateModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              style={{ backgroundColor: "#DBEAFE", color: "#1E3A8A" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
                style={{ color: "#2563EB" }}
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M5 4h4l3 3h7a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-11a2 2 0 0 1 2 -2" />
              </svg>
              <span className="font-medium text-sm">Certificate Templates</span>
            </button>
            
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 hover:scale-105 active:scale-95"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Document
            </button>
          </div>
        </div>

        {/* Statistics Summary */}
        <DocumentStatisticsCards stats={stats} documents={documents} />

        {/* Document Demographics Dashboard */}
        <DocumentDemographicsDashboard
          documents={documents}
          showDetails={showDemographics}
          stats={stats}
          toggleDetails={() => setShowDemographics(v => !v)}
        />

        <DocumentsTable
          documents={documents}
          isLoading={isLoading}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onTrack={handleTrack}
          onChangeStatus={handleChangeStatus}
          searchValue={search}
          onSearchChange={setSearch}
          onSearch={handleSearch}
          onFilter={handleFilterChange}
          pagination={{
            currentPage: page,
            lastPage,
            perPage,
            total,
          }}
          onPageChange={handlePageChange}
        />
        <DocumentStatusModal
          open={showStatusModal}
          onClose={() => { setShowStatusModal(false); setStatusTarget(null); }}
          onSubmit={handleStatusSubmit}
          currentStatus={statusTarget?.status}
          loading={statusLoading}
        />
        <DocumentFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={editData}
          loading={formLoading}
        />

        <ToastContainer />
        {/* Certificate Template Management Modal */}
        {showTemplateModal && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center" >
            <div className="bg-white rounded-lg shadow-lg w-full max-w-6xl relative p-6 max-h-[90vh] overflow-hidden">
              <div className="flex justify-between items-center mb-6 animate-in slide-in-from-top duration-500 delay-200">
                <h3 className="text-xl font-medium text-gray-900">Certificate Template</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setShowTemplateModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl transition-all duration-300 transform hover:scale-125 hover:rotate-90 active:scale-95"
                >
                  ×
                </button>
              </div>
              <CertificateTemplatesPage />
            </div>
          </div>
        )}

        {/* Certificate Print Preview Modal (View) */}
        {showPreviewModal && (
          <CertificatePrintPreviewModal
            open={showPreviewModal}
            onClose={() => { setShowPreviewModal(false); setPreviewDoc(null); }}
            document={previewDoc}
          />
        )}
        <ConfirmModal
          open={confirmOpen}
          title="Delete Document?"
          message="Are you sure you want to delete this document? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={() => { setConfirmOpen(false); setConfirmTarget(null); }}
          loading={confirmLoading}
          confirmText="Delete"
          cancelText="Cancel"
        />
      </div>
    </div>
  );
};

export default DocumentsPage;